import $ from 'jquery';
import 'jquery-ui/ui/position';
import h from 'hyperscript';
import { Dialog } from 'ui/dialog/dialog';
import './flyout.css';
import { on, unByKey } from 'cadenza/utils/event-util';

const NAME = 'd-flyout';

/**
 * A legacy flyout
 *
 * @deprecated - Use `ui/popup` instead.
 */
export class Flyout extends Dialog {

  #arrow;
  #content;
  #target;
  #closeOnInsideClick;
  #closeOnOutsideClick;
  #draggingOnFlyout;
  #eventKeys = [];

  constructor ({ displayArrow = true, closeOnInsideClick = false, closeOnOutsideClick = false, content, styleClass = '' } = {}) {
    super({ cancelOnBackdropClick: true });
    this.onwheel = (event) => event.stopPropagation();
    this.dialog.classList.add(NAME);
    if (displayArrow) {
      this.dialog.append(this.#arrow = h(`.${NAME}--arrow`));
    }
    this.progressSpinner = h('d-progress-spinner.d-flyout--progress-spinner.hidden');
    this.append(this.progressSpinner);
    this.#closeOnInsideClick = closeOnInsideClick;
    this.#closeOnOutsideClick = closeOnOutsideClick;
    this.#content = content;
    if (styleClass) {
      this.classList.add(styleClass);
    }
  }

  connectedCallback () {
    super.connectedCallback();
    this.#draggingOnFlyout = false;
    if (this.#closeOnInsideClick) {
      this.#eventKeys.push(
        // The three event handlers instead of a simple click handler are needed to prevent the flyout
        // from closing when text is selected inside it
        on(this, 'pointerdown', () => { this.#draggingOnFlyout = false; }),
        on(this, 'pointermove', () => { this.#draggingOnFlyout = true; }),
        on(this, 'click', () => {
          if (!this.#draggingOnFlyout) {
            this.close();
          }
        })
      );
    }
    if (this.#closeOnOutsideClick) {
      this.#eventKeys.push(on(document, 'click touchstart', event => this._onDocumentClick(event)));
    }
    if (this.#content) {
      this.dialog.append(this.#content);
    }
  }

  disconnectedCallback () {
    super.disconnectedCallback();
    unByKey(this.#eventKeys);
    if (this.#content) {
      this.dialog.removeChild(this.#content);
    }
  }

  _onDocumentClick (event) {
    const closestFlyout = event.target.closest('d-flyout');
    if (this.querySelector('dialog').hasAttribute('open') && (!closestFlyout || closestFlyout !== this)) {
      this.close();
    }
  }

  show (target, position, arrowLeftPositionFix = 5, offsetArrowPositioning, verticalOffset = 0) {
    const promise = super.show();
    this.#target = target;
    this.updatePosition(target, position, arrowLeftPositionFix, offsetArrowPositioning, verticalOffset);
    return promise;
  }

  showModal (target, position, arrowLeftPositionFix = 5) {
    const promise = super.showModal();
    this.#target = target;
    this.updatePosition(target, position, arrowLeftPositionFix);
    return promise;
  }

  showProgressSpinner () {
    this.progressSpinner.classList.toggle('hidden', false);
  }

  hideProgressSpinner () {
    this.progressSpinner.classList.toggle('hidden', true);
  }

  setContent (content, updatePosition = true) {
    if (this.#content && this.#content.parentElement === this.dialog) {
      this.dialog.removeChild(this.#content);
    }
    this.#content = content;
    this.dialog.append(content);
    if (updatePosition) {
      this.updatePosition();
    }
  }

  updatePosition (target = this.#target, {
    at = 'bottom',
    my = invert(at),
    collision = 'flipfit',
    offset,
    constrainHeight = false
  } = {},
  arrowLeftPositionFix,
  offsetArrowPositioning,
  verticalOffset = 0) {
    const of = target;
    let offsetFunction;
    if (this.#arrow) {
      const $arrow = $(this.#arrow);
      const arrowHeight = $arrow.outerHeight() / 2;
      const arrowWidth = $arrow.outerWidth() / 2;
      offsetFunction = _getOffsetFunction($arrow,
        my, at, of,
        offsetX(at, arrowWidth / 2),
        offsetY(at, _isEvent(of) ? arrowHeight * 2 : arrowHeight),
        arrowLeftPositionFix,
        offsetArrowPositioning,
        verticalOffset);
    } else if (offset) {
      offsetFunction = function ({ top, left }, axes) {
        $(this).css({
          top: top + (offset.top ? offset.top : 0),
          left: left + (offset.left ? offset.left : 0)
        });
      };
    } else {
      offsetFunction = null;
    }

    if (constrainHeight) {
      this._positionWithConstrainedHeight(target, offsetFunction);
    } else {
      $(this.dialog).position({
        my,
        at,
        of,
        collision,
        using: offsetFunction
      });
    }

    $(this.progressSpinner).css('top', $(this.dialog).css('top'));
    $(this.progressSpinner).css('left', $(this.dialog).css('left'));
    $(this.progressSpinner).height($(this.dialog).outerHeight());
    $(this.progressSpinner).width($(this.dialog).outerWidth());
  }

  /**
   * Ad-hoc hack function implemented to fulfill the requirements of UX-51:
   * The height of the menu should be constrained so that the menu does not overflow its container.
   * Due to the (lack of) architecture of this flyout component it was hard to implement in a nice general way,
   * so the following logic applies, regardless of the actual positioning parameters received:
   * - Default position is 'my left top at left bottom'
   * - Vertical flip if not enough space under the target but enough space above
   * - If not enough space in both directions, menu will be positioned in the bigger of the two,
   * and the height constrained to the available space
   * - Container to measure space is hard-wired to '#content'
   *
   * @param target - Target element (typically the button that was clicked)
   * @param offsetFunction - Offset function
   * @private
   */
  _positionWithConstrainedHeight (target, offsetFunction) {
    const container = document.getElementById('content');
    const { top: containerTop, height: containerHeight } = container.getBoundingClientRect();
    const { top: targetTop, height: targetHeight } = target.getBoundingClientRect();
    const thisHeight = this.dialog.getBoundingClientRect().height;
    const spaceBeforeTarget = targetTop - containerTop;
    const spaceAfterTarget = containerHeight - (spaceBeforeTarget + targetHeight);

    const [ availableSpace, positionAbove ] = determinePositionAndAvailableSpace(thisHeight, spaceBeforeTarget, spaceAfterTarget);
    this.dialog.style.maxHeight = `${availableSpace - 10}px`; // 10px = vertical padding of .d-action-buttons, so the flyout does not go exactly till the end of the container, but is a bit smaller.

    $(this.dialog).position({
      my: positionAbove ? 'left bottom' : 'left top',
      at: positionAbove ? 'left top' : 'left bottom',
      of: target,
      collision: 'none',
      using: offsetFunction
    });
  }

}

function invert (at) {
  return ({
    'top': 'bottom',
    'right': 'left',
    'bottom': 'top',
    'left': 'right',
    'center': 'center',
    'left bottom': 'left top',
    'left top': 'left bottom',
    'right bottom': 'right top',
    'right top': 'right bottom',
    'center bottom': 'center top',
    'center top': 'center bottom',
    'left middle': 'right middle',
    'right middle': 'left middle'
  })[at];
}

function _getOffsetFunction ($arrow, my, at, of, x, y, arrowLeftPositionFix, offsetArrowPositioning, verticalOffset) {
  return function ({ top, left }, axes) {
    const potentialFlipAxis1 = axes.important === 'vertical' ? 'horizontal' : 'vertical';
    const flipped1 = !my.includes(axes[potentialFlipAxis1]) && axes[potentialFlipAxis1] !== 'center' && axes[potentialFlipAxis1] !== 'middle';

    const potentialFlipAxis2 = axes.important === 'vertical' ? 'vertical' : 'horizontal';
    const flipped2 = !my.includes(axes[potentialFlipAxis2]);

    const flippedX = (axes.important === 'vertical' && flipped1) || (axes.important === 'horizontal' && flipped2);
    const flippedY = (axes.important === 'horizontal' && flipped1) || (axes.important === 'vertical' && flipped2);

    $(this).css({
      top: flippedY ? top - y - verticalOffset : top + y + verticalOffset,
      left: flippedX ? left - x : left + x
    });

    const arrowMy = `${at.includes('bottom') || at.includes('top') ? 'center' : axes.horizontal} ${axes.vertical}`;
    const arrowAt = invert(arrowMy);
    const arrowHeight = $arrow.outerHeight();
    const position = {
      my: arrowMy,
      at: arrowAt,
      of
    };
    if (_isEvent(of) || (!_isEvent(of) && axes.important === 'vertical' && !flippedX && !flippedY && at === 'top' && my === 'bottom')) {
      position.collision = 'none';
      position.using = arrowOffset(0, arrowOffsetY(at, arrowHeight / 2), flippedX, flippedY);
    } else {
      position.using = function ({ top, left }) {
        $(this).css({
          top: flippedY ? top + (arrowHeight / 2) - verticalOffset : top - (arrowHeight / 2) + verticalOffset,
          left: flippedX ? left - arrowLeftPositionFix : left + arrowLeftPositionFix
        });
      };
    }
    let arrowRotation = arrowRotate(arrowAt);
    if (offsetArrowPositioning) {
      const arrowPositionForce = offsetArrowPositioning(flippedX, flippedY);
      arrowRotation = arrowPositionForce.arrowRotationForce || arrowRotation;
      position.my = arrowPositionForce.my;
      position.at = arrowPositionForce.at;
    }
    $arrow.css('transform', `rotate(${arrowRotation})`).position(position);
  };
}

function _isEvent (obj) {
  return (obj instanceof Event || obj instanceof $.Event);
}

function arrowOffset (x, y, flippedX, flippedY) {
  return function ({ top, left }) {
    $(this).css({
      top: flippedY ? top - y : top + y,
      left: flippedX ? left - x : left + x
    });
  };
}

function arrowOffsetY (at, offset) {
  if (at.includes('left') || at.includes('right')) {
    return -offset;
  }
  if (at.includes('top')) {
    return offset;
  }
  return 0;
}

function offsetX (at, offset) {
  if (at.includes('left')) {
    return -offset;
  }
  if (at.includes('right')) {
    return offset;
  }
  return 0;
}

function offsetY (at, offset) {
  if (at.includes('top')) {
    return -offset;
  } else if (at.includes('bottom')) {
    return offset;
  }
  return 0;
}

function arrowRotate (at) {
  return ({
    'top': '180deg',
    'right': '270deg',
    'bottom': '0',
    'left': '90deg',
    'left bottom': '0',
    'left top': '180deg',
    'right bottom': '0',
    'right top': '180deg',
    'center bottom': '0',
    'center top': '180deg',
    'left middle': '90deg',
    'right middle': '270deg'
  })[at];
}

function determinePositionAndAvailableSpace (height, spaceAbove, spaceUnder) {
  const availableSpace = Math.max(spaceAbove, spaceUnder);
  const positionAbove = height > spaceUnder && spaceAbove > spaceUnder;
  return [ availableSpace, positionAbove ];
}

customElements.define(NAME, Flyout);
